import {
  Link,
  type LinkOptions,
  useNavigate,
  useRouter,
  useSearch,
} from "@tanstack/react-router";
import type { ComponentPropsWithoutRef } from "react";

import { cn } from "@repo/cn";
import type { SVGIconProps } from "@repo/icons/interface";

import type { AlignProps } from "@/lib/interfaces/align-props";

interface HeaderButtonProps extends ComponentPropsWithoutRef<"button">, AlignProps {
  icon: React.ElementType<SVGIconProps>;
}

interface HeaderTextButtonProps extends ComponentPropsWithoutRef<"button">, AlignProps {
  text: string;
}

interface HeaderLinkButtonProps extends HeaderButtonProps {
  icon: React.ElementType<SVGIconProps>;
  to: LinkOptions["to"];
}

export const HeaderButton = ({
  icon: Icon,
  onClick,
  ...rest
}: HeaderButtonProps): React.JSX.Element => {
  const router = useRouter();

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    if (onClick) {
      onClick(event);
    } else {
      router.history.back();
    }
  };

  return (
    <button type="button" {...rest} onClick={handleClick}>
      <Icon size={24} className="fill-white" />
    </button>
  );
};

export const HeaderIcon = ({
  icon: Icon,
  className,
  ...rest
}: HeaderButtonProps): React.JSX.Element => {
  return (
    <button type="button" {...rest}>
      <Icon size={24} className={cn("fill-white", className)} />
    </button>
  );
};

export const HeaderTextButton = ({
  text,
  onClick,
  className,
  ...rest
}: HeaderTextButtonProps): React.JSX.Element => {
  const router = useRouter();

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    if (onClick) {
      onClick(event);
    } else {
      router.history.back();
    }
  };

  return (
    <button
      type="button"
      {...rest}
      className={cn("text-sm text-white", className)}
      onClick={handleClick}
    >
      {text}
    </button>
  );
};

export const HeaderLinkButton = ({
  icon: Icon,
  to,
}: HeaderLinkButtonProps): React.JSX.Element => {
  return (
    <Link to={to}>
      <Icon size={24} className="fill-white" />
    </Link>
  );
};

export const HeaderUpButton = ({
  icon: Icon,
  ...rest
}: HeaderButtonProps): React.JSX.Element => {
  const navigate = useNavigate();
  const params: object = useSearch({ strict: false });

  return (
    <button
      type="button"
      {...rest}
      onClick={() => {
        void navigate({ to: "..", search: { ...params } });
      }}
    >
      <Icon size={24} className="fill-white" />
    </button>
  );
};

interface HeaderTitleProps extends AlignProps {
  title: string;
}
export const HeaderTitle = ({ title }: HeaderTitleProps): React.JSX.Element => {
  return <h1 className="text-lg text-white max-w-[300px] truncate">{title}</h1>;
};

interface HeaderBodyProps extends AlignProps, ComponentPropsWithoutRef<"div"> {}
export const HeaderBody = ({
  children,
  className,
  ...rest
}: HeaderBodyProps): React.JSX.Element => {
  return (
    <div className={cn("w-full px-4", className)} {...rest}>
      {children}
    </div>
  );
};

interface HeaderRootProps {
  children?:
    | React.ReactElement<
        HeaderButtonProps | HeaderTitleProps | HeaderBodyProps | HeaderTextButtonProps
      >
    | React.ReactElement<
        HeaderButtonProps | HeaderTitleProps | HeaderBodyProps | HeaderTextButtonProps
      >[];
  className?: string;
}

export const HeaderRoot = ({
  children,
  className,
}: HeaderRootProps): React.JSX.Element => {
  // biome-ignore lint/suspicious/noImplicitAnyLet: legacy code
  let startItems, endItems, centerItems, bottomItem;
  if (Array.isArray(children)) {
    startItems = children.filter((child) => child.props.align === "start");
    endItems = children.filter((child) => child.props.align === "end");
    bottomItem = children.filter((child) => child.props.align === "bottom");
    centerItems = children.filter(
      (child) => child.props.align === "center" || !child.props.align,
    );
  } else if (children?.props.align === "start") {
    startItems = children;
  } else if (children?.props.align === "end") {
    endItems = children;
  } else if (children?.props.align === "bottom") {
    bottomItem = children;
  } else {
    centerItems = children;
  }

  const hasBottomItem = Array.isArray(bottomItem)
    ? bottomItem.length > 0
    : Boolean(bottomItem);

  return (
    <header className={cn("bg-primary-400 z-20", className)}>
      <div className="flex flex-row justify-between items-center gap-3 px-4 h-14">
        <div className="flex absolute left-4 gap-4">{startItems}</div>
        <div className="flex justify-center gap-3 w-full">{centerItems}</div>
        <div className="flex absolute right-4 gap-4">{endItems}</div>
      </div>
      {hasBottomItem ? (
        <div className="flex flex-row justify-center items-center mx-4 mb-4 pb-2">
          {bottomItem}
        </div>
      ) : null}
    </header>
  );
};
